import React, { useState, useRef } from 'react';

interface WorkDaysFilterDropdownProps {
  show: boolean;
  selectedWorkdayTypes: string[];
  onClose: () => void;
  onApply: (workdayTypes: string[]) => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
}

const workdayOptions = [
  { value: 'five_days_in_a_week', label: '週5日' },
  { value: 'four_days_in_a_week', label: '週4日' },
  { value: 'three_days_in_a_week', label: '週3日' },
  { value: 'two_days_in_a_week', label: '週2日' },
  { value: 'one_day_in_a_week', label: '週1日' }
];

const WorkDaysFilterDropdown: React.FC<WorkDaysFilterDropdownProps> = ({
  show,
  selectedWorkdayTypes,
  onClose,
  onApply,
  buttonRef
}) => {
  const [currentSelection, setCurrentSelection] = useState<string[]>(selectedWorkdayTypes);
  const dropdownRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (show) {
      setCurrentSelection(selectedWorkdayTypes);
    }
  }, [show, selectedWorkdayTypes]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onClose, buttonRef]);

  const handleCheckboxChange = (workdayType: string) => {
    const newSelection = currentSelection.includes(workdayType)
      ? currentSelection.filter(type => type !== workdayType)
      : [...currentSelection, workdayType];
    
    setCurrentSelection(newSelection);
    
    // 勤務日数選択時に自動的に検索を実行
    onApply(newSelection);
  };

  const handleApply = () => {
    onApply(currentSelection);
    onClose();
  };

  if (!show) return null;

  return (
    <div 
      ref={dropdownRef}
      className="position-absolute bg-white border rounded shadow-lg" 
      style={{ 
        top: '100%', 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        maxHeight: '400px',
        overflowY: 'auto',
        minWidth: '500px'
      }}
    >
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">週日数で絞り込み</h6>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        
        <ul className="list-group list-group-flush">
          {workdayOptions.map(option => (
            <li key={option.value} className="list-group-item">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`workday-${option.value}`}
                  checked={currentSelection.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                />
                <label className="form-check-label" htmlFor={`workday-${option.value}`}>
                  {option.label}
                </label>
              </div>
            </li>
          ))}
        </ul>
        
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            キャンセル
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleApply}>
            この条件で検索
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkDaysFilterDropdown;
